/* eslint-disable react/prop-types */
import React from 'react';
import { Box } from 'rebass';
import { useStore } from 'effector-react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { Trans, t } from '@lingui/macro';
import { CommonPageTemplate, $allEmployeesList } from '@features/common';
import { i18n } from '@lib/i18n';
import { Button, Typography, DataTable, Tabs, Tab } from '@oca/ui';
import { Plus } from '@oca/icons';

import {
  PAYROLL_PAGE_SIZE,
  $payrollRequests,
  $payrollCount,
  $payrollFetching,
  fetchPayrollRequestsRequest,
  openCreateModal,
  openDecisionModal,
} from '../model/payroll';
import { RequestModal } from '../organisms/request-modal';
import { DecisionModal } from '../organisms/decision-modal';

const monthNames = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const statusTabs = [
  { value: '', label: <Trans>All</Trans> },
  { value: 'pending', label: <Trans>Pending</Trans> },
  { value: 'approved', label: <Trans>Approved</Trans> },
  { value: 'rejected', label: <Trans>Rejected</Trans> },
];

const statusStyles = {
  pending: {
    bg: '#fffbe6',
    color: '#d46b08',
    border: '#ffe58f',
    label: <Trans>Pending</Trans>,
  },
  approved: {
    bg: '#f6ffed',
    color: '#389e0d',
    border: '#b7eb8f',
    label: <Trans>Approved</Trans>,
  },
  rejected: {
    bg: '#fff1f0',
    color: '#cf1322',
    border: '#ffa39e',
    label: <Trans>Rejected</Trans>,
  },
};

const formatPeriod = d => `${monthNames[d.month] || d.month} ${d.year}`;

export const PayrollListPage = () => {
  const requests = useStore($payrollRequests);
  const count = useStore($payrollCount);
  const { isLoading, isFail } = useStore($payrollFetching);
  const employees = useStore($allEmployeesList);
  const [status, setStatus] = React.useState('');
  const [page, setPage] = React.useState(0); // react-table pages are 0-based

  const load = React.useCallback(() => {
    fetchPayrollRequestsRequest({
      page: page + 1,
      limit: PAYROLL_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
  }, [page, status]);

  React.useEffect(() => {
    load();
  }, [load]);

  const changeStatus = value => {
    setStatus(value);
    setPage(0);
  };

  // The API only returns employee_id; resolve names from the shared list.
  const employeeNames = React.useMemo(
    () =>
      employees.reduce((acc, e) => {
        acc[e.id] = e.fullName;
        return acc;
      }, {}),
    [employees],
  );
  const getEmployeeName = React.useCallback(
    d => employeeNames[d.employeeId] || i18n._(t`Employee #${d.employeeId}`),
    [employeeNames],
  );

  const columns = React.useMemo(
    () => [
      {
        Header: <Trans>Employee</Trans>,
        id: 'employee',
        accessor: getEmployeeName,
        Cell: ({ value }) => (
          <Typography fontWeight={500} color="text">
            {value}
          </Typography>
        ),
      },
      {
        Header: <Trans>Period</Trans>,
        id: 'period',
        accessor: formatPeriod,
        maxWidth: 160,
      },
      {
        Header: <Trans>Reason</Trans>,
        id: 'reason',
        accessor: d => d.reason || '-',
      },
      {
        Header: <Trans>Requested</Trans>,
        id: 'createdAt',
        accessor: d =>
          d.createdAt ? dayjs(d.createdAt).format('YYYY-MM-DD') : '-',
        maxWidth: 130,
      },
      {
        Header: <Trans>Status</Trans>,
        id: 'status',
        accessor: 'status',
        maxWidth: 220,
        Cell: ({ original }) => {
          const style = statusStyles[original.status] || statusStyles.pending;
          return (
            <Box>
              <Badge bg={style.bg} color={style.color} border={style.border}>
                {style.label}
              </Badge>
              {original.decidedAt && (
                <Typography fontSize={12} color="textSecondary" mt={1}>
                  {dayjs(original.decidedAt).format('YYYY-MM-DD')}
                </Typography>
              )}
              {original.status === 'rejected' && original.rejectionReason && (
                <Typography fontSize={12} color="textSecondary" mt={1}>
                  {original.rejectionReason}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        Header: <Trans>Actions</Trans>,
        id: 'actions',
        maxWidth: 120,
        // `canDecide` comes from the backend: true only for an authorized
        // reviewer on someone else's pending request.
        Cell: ({ original }) =>
          original.canDecide ? (
            <Button
              size="small"
              variant="secondary"
              onClick={() => openDecisionModal(original)}
            >
              <Trans>Review</Trans>
            </Button>
          ) : null,
      },
    ],
    [getEmployeeName],
  );

  const renderContent = () => {
    if (isFail) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="error" mb={3}>
            <Trans>
              Unable to retrieve payroll requests. Please try again later.
            </Trans>
          </Typography>
          <Button variant="secondary" onClick={load} disabled={isLoading}>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      );
    }
    return (
      <DataTable
        manual
        data={requests}
        columns={columns}
        loading={isLoading}
        page={page}
        pages={Math.max(1, Math.ceil(count / PAYROLL_PAGE_SIZE))}
        onPageChange={setPage}
        pageSize={PAYROLL_PAGE_SIZE}
        showPagination={count > PAYROLL_PAGE_SIZE}
        showPageSizeOptions={false}
        sortable={false}
        minRows={0}
        noDataText={
          <Box p={4} textAlign="center">
            <Typography fontSize={18} color="textSecondary" mb={2}>
              <Trans>No payroll slip requests found</Trans>
            </Typography>
            <Typography fontSize={14} color="textSecondary">
              <Trans>
                Use &quot;Request Payroll Slip&quot; to submit a new request.
              </Trans>
            </Typography>
          </Box>
        }
      />
    );
  };

  return (
    <CommonPageTemplate
      contentWidth={1}
      title={i18n._(t`Payroll Slip Requests`)}
      heading={i18n._(t`Payroll Slip Requests`)}
      actions={
        <Button variant="primary" onClick={openCreateModal}>
          <Plus />
          <Trans>Request Payroll Slip</Trans>
        </Button>
      }
    >
      <Box px={2} pt={3}>
        <Tabs value={status} onChange={changeStatus} tabs={statusTabs}>
          {statusTabs.map(tab => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Box px={2} py={4}>
        {renderContent()}
      </Box>
      <RequestModal />
      <DecisionModal
        getEmployeeName={getEmployeeName}
        formatPeriod={formatPeriod}
      />
    </CommonPageTemplate>
  );
};

const Badge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ bg }) => bg};
  color: ${({ color }) => color};
  border: 1px solid ${({ border }) => border};
`;
